import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Member } from '../types';
import {
  User,
  Phone,
  MessageCircle,
  UserCheck,
  Clipboard,
  Target,
  Zap,
  FileText,
  ChevronDown,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Sparkles,
  Database,
  Users,
  Calendar,
  ThumbsUp,
  ThumbsDown,
  Edit,
  FileCheck,
  Clock,
  Shield,
} from 'lucide-react';
import { Badge, PriorityBadge, ConfidenceBadge, EffortBadge } from '../components/ui/Badge';
import { ProgressBar } from '../components/ui/ProgressBar';
import { Pill, TimeframePill } from '../components/ui/Pill';
import { Card } from '../components/ui/Card';

interface AIInsight {
  id: string;
  insight_type: string;
  severity: string;
  title: string;
  description: string;
  confidence_score: number;
  data_sources: string[];
}

interface Intervention {
  id: string;
  category: string;
  title: string;
  description: string;
  effort_level: 'low' | 'medium' | 'high';
  confidence_score: number;
  impact_description: string;
  evidence_citation: string;
  status: string;
}

interface CareTeamMember {
  id: string;
  name: string;
  role: string;
  title: string;
  responsibilities: string;
}

interface CarePlan {
  id: string;
  plan_type: string;
  confidence_score: number;
  status: string;
  effective_date: string;
  review_date: string;
  problems_count: number;
}

interface CarePlanGoal {
  id: string;
  problem_code: string;
  problem_name: string;
  goal_text: string;
  timeframe_days: number;
  priority: string;
  completed: boolean;
}

interface DataSource {
  source_name: string;
  record_count: number;
}

export default function Member360Enhanced() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [activeTab, setActiveTab] = useState<'assessment' | 'careplan' | 'interventions' | 'documentation'>('assessment');
  const [aiInsights, setAiInsights] = useState<AIInsight[]>([]);
  const [interventions, setInterventions] = useState<Intervention[]>([]);
  const [careTeam, setCareTeam] = useState<CareTeamMember[]>([]);
  const [carePlan, setCarePlan] = useState<CarePlan | null>(null);
  const [carePlanGoals, setCarePlanGoals] = useState<CarePlanGoal[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (memberId) {
      fetchMemberData(memberId);
    }
  }, [memberId]);

  const fetchMemberData = async (memberIdParam: string) => {
    try {
      setLoading(true);
      const { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('*')
        .eq('member_id', memberIdParam)
        .maybeSingle();

      if (memberError) throw memberError;
      setMember(memberData);

      if (memberData) {
        const [insightsRes, interventionsRes, teamRes, planRes, goalsRes, sourcesRes] =
          await Promise.all([
            supabase
              .from('ai_insights')
              .select('*')
              .eq('member_id', memberData.id)
              .limit(3),
            supabase
              .from('interventions')
              .select('*')
              .eq('member_id', memberData.id)
              .eq('status', 'pending')
              .order('confidence_score', { ascending: false }),
            supabase
              .from('care_team_members')
              .select('*')
              .eq('member_id', memberData.id)
              .eq('active', true),
            supabase
              .from('care_plans')
              .select('*')
              .eq('member_id', memberData.id)
              .eq('status', 'active')
              .maybeSingle(),
            supabase
              .from('care_plan_goals')
              .select('*, care_plans!inner(member_id)')
              .eq('care_plans.member_id', memberData.id)
              .limit(10),
            supabase.from('data_sources').select('source_name, record_count'),
          ]);

        if (insightsRes.data) setAiInsights(insightsRes.data);
        if (interventionsRes.data) setInterventions(interventionsRes.data);
        if (teamRes.data) setCareTeam(teamRes.data);
        if (planRes.data) setCarePlan(planRes.data);
        if (goalsRes.data) setCarePlanGoals(goalsRes.data);
        if (sourcesRes.data) setDataSources(sourcesRes.data);
      }
    } catch (error) {
      console.error('Error fetching member data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const getRiskScoreColor = (score: number) => {
    if (score >= 4) return 'text-red-600';
    if (score >= 3) return 'text-orange-600';
    if (score >= 2) return 'text-amber-600';
    return 'text-green-600';
  };

  const getRiskPriority = (score: number): 'high' | 'medium' | 'low' | 'critical' => {
    if (score >= 4.5) return 'critical';
    if (score >= 4) return 'high';
    if (score >= 3) return 'medium';
    return 'low';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading member data...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Member not found</p>
      </div>
    );
  }

  const assessmentCompletion = 68;
  const nextContactDate = '2024-12-16';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-purple-100 p-3 rounded-lg">
              <User className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-900">
                  {member.first_name} {member.last_name}
                </h1>
                <PriorityBadge priority={getRiskPriority(member.risk_score)} />
              </div>
              <p className="text-sm text-gray-600 mt-0.5">
                {member.member_id} • {new Date().getFullYear() - new Date(member.date_of_birth).getFullYear()}yo {member.date_of_birth.split('-')[0] > '2000' ? 'M' : 'F'} • {member.program || 'Medicaid-TX'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 text-gray-700">
              <Phone className="w-4 h-4" />
              <span className="text-sm">{member.phone || '(512) 555-0147'}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm">{member.preferred_language}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-700">
              <UserCheck className="w-4 h-4" />
              <span className="text-sm">{member.assigned_care_manager || 'Dr. Sarah Mitchell'}</span>
            </div>
          </div>

          <div className="flex items-center gap-8">
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-1">Risk Score</p>
              <div className="flex items-baseline gap-1">
                <span className={`text-3xl font-bold ${getRiskScoreColor(member.risk_score)}`}>
                  {member.risk_score.toFixed(1)}
                </span>
                <TrendingUp className="w-4 h-4 text-red-500" />
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-1">Next Contact</p>
              <p className="text-base font-semibold text-purple-600">{nextContactDate}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-8">
        <nav className="flex gap-8">
          {[
            { id: 'assessment', label: 'Assessment', icon: Clipboard, badge: `${assessmentCompletion}%` },
            { id: 'careplan', label: 'Care Plan', icon: Target },
            { id: 'interventions', label: 'Interventions', icon: Zap, badge: interventions.length },
            { id: 'documentation', label: 'Documentation', icon: FileText },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 py-4 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium text-sm">{tab.label}</span>
                {tab.badge && (
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                      activeTab === tab.id
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content Area */}
      <div className="px-8 py-6">
        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Assessment Tab */}
            {activeTab === 'assessment' && (
              <>
                {/* Progress */}
                <Card>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Assessment Completion</h2>
                    <span className="text-2xl font-bold text-gray-900">{assessmentCompletion}%</span>
                  </div>
                  <ProgressBar value={assessmentCompletion} variant="success" size="lg" className="[&>div>div]:bg-purple-600" />
                  <p className="text-sm text-gray-600 mt-3 flex items-center gap-1">
                    <Sparkles className="w-4 h-4" />
                    AI has pre-filled {assessmentCompletion}% of fields from claims, EHR, and previous assessments
                  </p>
                </Card>

                {/* Health Status */}
                <Card>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Clipboard className="w-5 h-5 text-green-600" />
                      <h2 className="text-lg font-semibold text-gray-900">Health Status Assessment</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">4 fields</span>
                      <ConfidenceBadge score={94} />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Active Chronic Conditions</label>
                        <button className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                          <span>Source: Claims + EHR</span>
                          <Edit className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Pill variant="primary">Type 2 Diabetes</Pill>
                        <Pill variant="primary">CHF (NYHA Class II)</Pill>
                        <Pill variant="primary">CKD Stage 3b</Pill>
                        <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">+ Add</button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Extracted from recent claims (ICD-10 codes) and EHR problem list. CHF class confirmed via recent cardiology note.
                      </p>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">Current Symptoms Reported</label>
                        <button className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1">
                          <span>Source: EHR Clinical Notes</span>
                          <Edit className="w-3 h-3" />
                        </button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Pill variant="warning">Fatigue</Pill>
                        <Pill variant="warning">Shortness of breath on exertion</Pill>
                        <Pill variant="warning">Lower extremity edema</Pill>
                        <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">+ Add</button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Symptoms extracted from ED visit note dated 11/28. Member reported these during triage.
                      </p>
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* Care Plan Tab */}
            {activeTab === 'careplan' && carePlan && (
              <>
                {/* Care Plan Header */}
                <div className="bg-gradient-to-r from-purple-600 to-purple-700 rounded-xl p-6 text-white">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-white/20 p-3 rounded-lg">
                        <Target className="w-8 h-8" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold mb-1">{carePlan.plan_type}</h2>
                        <p className="text-blue-100">
                          AI-Generated Care Plan • {Math.round(carePlan.confidence_score)}% Confidence
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg font-medium transition-colors flex items-center gap-2">
                        <Edit className="w-4 h-4" />
                        Edit Plan
                      </button>
                      <button className="px-4 py-2 bg-white text-purple-600 hover:bg-purple-50 rounded-lg font-medium transition-colors flex items-center gap-2">
                        <FileCheck className="w-4 h-4" />
                        Approve & Sign
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-6 mt-6">
                    <div>
                      <p className="text-blue-100 text-sm mb-1">Effective Date</p>
                      <p className="text-lg font-semibold">{formatDate(carePlan.effective_date)}</p>
                    </div>
                    <div>
                      <p className="text-blue-100 text-sm mb-1">Review Date</p>
                      <p className="text-lg font-semibold">{formatDate(carePlan.review_date)}</p>
                    </div>
                    <div>
                      <p className="text-blue-100 text-sm mb-1">Problems Addressed</p>
                      <p className="text-lg font-semibold">{carePlan.problems_count} Active</p>
                    </div>
                  </div>
                </div>

                {/* Care Plan Goals */}
                <Card>
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded">HIGH</span>
                      <h3 className="text-lg font-bold text-gray-900">Heart Failure Management (I50.9)</h3>
                    </div>
                    <Badge variant="success">active</Badge>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Target className="w-5 h-5 text-purple-600" />
                      <h4 className="font-semibold text-gray-900">Goals</h4>
                    </div>
                    <div className="space-y-3">
                      {carePlanGoals.slice(0, 3).map((goal) => (
                        <div key={goal.id} className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={goal.completed}
                            className="mt-1 w-4 h-4 rounded border-gray-300"
                            readOnly
                          />
                          <div className="flex-1">
                            <p className="text-sm text-gray-900">{goal.goal_text}</p>
                          </div>
                          <TimeframePill days={goal.timeframe_days} />
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* Interventions Tab */}
            {activeTab === 'interventions' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">AI-Recommended Interventions</h2>
                    <p className="text-sm text-gray-600">Review and accept or decline suggested interventions</p>
                  </div>
                  <span className="text-sm text-gray-600">{interventions.filter(i => i.status === 'pending').length} pending review</span>
                </div>

                <div className="space-y-4">
                  {interventions.map((intervention) => (
                    <Card key={intervention.id}>
                      <div className="flex items-start gap-4">
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <Sparkles className="w-6 h-6 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                  {intervention.category.replace('_', ' ')}
                                </span>
                                <EffortBadge level={intervention.effort_level} />
                                <ConfidenceBadge score={Math.round(intervention.confidence_score)} />
                              </div>
                              <h3 className="text-base font-semibold text-gray-900">{intervention.title}</h3>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 mb-3">{intervention.description}</p>
                          <div className="flex items-center gap-4 text-xs text-gray-600 mb-3">
                            <div className="flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              <span>Impact: {intervention.impact_description}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <FileText className="w-3 h-3" />
                              <span>{intervention.evidence_citation}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm font-medium">
                              <ThumbsUp className="w-4 h-4" />
                              Accept
                            </button>
                            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 text-sm font-medium">
                              <ThumbsDown className="w-4 h-4" />
                              Decline
                            </button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                {/* Intervention Summary */}
                <Card className="bg-gray-50">
                  <h3 className="font-semibold text-gray-900 mb-4">Intervention Summary</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Accepted</p>
                      <p className="text-3xl font-bold text-green-600">0</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Declined</p>
                      <p className="text-3xl font-bold text-gray-600">0</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Pending</p>
                      <p className="text-3xl font-bold text-amber-600">{interventions.length}</p>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-start gap-2">
                      <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm mb-1">How AI Selects Interventions</h4>
                        <ul className="text-xs text-gray-600 space-y-1">
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600" />
                            Analyzes member's specific conditions and risk factors
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600" />
                            Matches interventions to evidence-based guidelines
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600" />
                            Considers SDOH barriers and practical feasibility
                          </li>
                          <li className="flex items-center gap-2">
                            <CheckCircle className="w-3 h-3 text-purple-600" />
                            Ranks by expected impact and effort required
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>
              </>
            )}

            {/* Documentation Tab */}
            {activeTab === 'documentation' && (
              <>
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">Generate Documentation</h2>
                  <p className="text-sm text-gray-600">Choose a documentation template above to auto-generate clinical notes</p>
                </div>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { icon: Clipboard, title: 'Initial Assessment Note', category: 'Assessment' },
                    { icon: Target, title: 'Care Plan Update', category: 'Care Plan' },
                    { icon: Phone, title: 'Telephonic Encounter', category: 'Progress Note' },
                    { icon: Users, title: 'Care Coordination Note', category: 'Coordination' },
                  ].map((template) => {
                    const Icon = template.icon;
                    return (
                      <button
                        key={template.title}
                        className="p-4 bg-white border-2 border-gray-200 rounded-xl hover:border-purple-400 hover:bg-purple-50 transition-all text-left group"
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <div className="bg-purple-100 p-2 rounded-lg group-hover:bg-purple-200 transition-colors">
                            <Icon className="w-5 h-5 text-purple-600" />
                          </div>
                          <Sparkles className="w-4 h-4 text-amber-500" />
                        </div>
                        <h3 className="font-semibold text-gray-900 mb-1">{template.title}</h3>
                        <p className="text-sm text-gray-600">{template.category}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a Template</h3>
                  <p className="text-sm text-gray-600">Choose a documentation template above to auto-generate clinical notes</p>
                </div>

                <div className="grid grid-cols-2 gap-6 mt-6">
                  <Card className="bg-green-50 border-green-200">
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-green-600" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-1">Documentation Time Saved</h4>
                        <p className="text-3xl font-bold text-green-600">~25 min</p>
                        <p className="text-xs text-gray-600 mt-2">AI auto-generated note from assessment data, care plan, and member information</p>
                      </div>
                    </div>
                  </Card>

                  <Card>
                    <div className="flex items-start gap-3">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">Compliance Check</h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span>NCQA CM Standards</span>
                          </div>
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span>State Medicaid Requirements</span>
                          </div>
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span>HIPAA Compliant</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                <Card className="bg-purple-50 border-purple-200">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-purple-600" />
                    <h4 className="font-semibold text-gray-900">Documentation Checklist</h4>
                  </div>
                  <div className="space-y-2 text-sm">
                    {[
                      'Initial Assessment',
                      'Care Plan Created',
                      'Goals Documented',
                      'Interventions Assigned',
                      'Next Contact Scheduled',
                    ].map((item) => (
                      <div key={item} className="flex items-center gap-2 text-green-700">
                        <CheckCircle className="w-4 h-4" />
                        <span>{item}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 text-gray-400">
                      <div className="w-4 h-4 border-2 border-gray-300 rounded" />
                      <span>Supervisor Review</span>
                    </div>
                  </div>
                </Card>
              </>
            )}
          </div>

          {/* Right Sidebar */}
          <div className="space-y-6">
            {/* AI Assistant Panel - Only show on Assessment tab */}
            {activeTab === 'assessment' && (
              <Card className="bg-gradient-to-br from-purple-600 to-purple-700 text-white">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5" />
                  <h3 className="font-semibold">AI Assessment Assistant</h3>
                </div>
                <p className="text-sm text-purple-100 mb-4">
                  I've analyzed claims, EHR data, and previous assessments to pre-fill this form. Review and validate each section.
                </p>

                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold mb-2">Key Findings:</p>
                    <div className="space-y-2">
                      {aiInsights.map((insight) => (
                        <div key={insight.id} className="flex items-start gap-2 text-sm">
                          {insight.severity === 'critical' ? (
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          ) : insight.severity === 'warning' ? (
                            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          ) : (
                            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                          )}
                          <span className="text-purple-50">{insight.title}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Care Team - Only show on Care Plan tab */}
            {activeTab === 'careplan' && (
              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Care Team</h3>
                </div>
                <div className="space-y-3">
                  {careTeam.map((member) => (
                    <div key={member.id} className="flex items-start gap-3">
                      <div className="bg-purple-100 p-2 rounded-lg">
                        <User className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">{member.name}</p>
                        <p className="text-xs text-purple-600 font-medium">{member.role}</p>
                        <p className="text-xs text-gray-600 mt-1">{member.responsibilities}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Data Sources */}
            <Card>
              <h3 className="font-semibold text-gray-900 mb-4">Data Sources Used</h3>
              <div className="space-y-3">
                {dataSources.map((source) => (
                  <div key={source.source_name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span className="text-sm text-gray-700">{source.source_name}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-900">
                      {source.record_count.toLocaleString()} records
                    </span>
                  </div>
                ))}
              </div>
            </Card>

            {/* Scheduled Contacts - Only show on Care Plan tab */}
            {activeTab === 'careplan' && (
              <Card>
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-purple-600" />
                  <h3 className="font-semibold text-gray-900">Scheduled Contacts</h3>
                </div>
                <div className="space-y-3">
                  <div className="p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-900">Follow-up Call</span>
                      <span className="text-xs text-purple-600">Tomorrow</span>
                    </div>
                    <p className="text-xs text-gray-600">Review medication adherence and CHF symptoms</p>
                  </div>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
